import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Database, FileText, CheckCircle2, 
  Building2, Users, Package, ChevronRight, Download, AlertCircle,
  Loader2, Info, ArrowLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Alert, AlertDescription, AlertTitle
} from "../../../components/ui/alert";

const ImportacionSaldos = () => {
  const [paso, setPaso] = useState(1);
  const [origenDatos, setOrigenDatos] = useState(null);
  const [tipoImportacion, setTipoImportacion] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [progreso, setProgreso] = useState(0);

  const origenes = [
    { 
      id: "excel", 
      nombre: "Microsoft Excel", 
      descripcion: "Archivos .xlsx o .xls exportados de otro sistema",
      icon: FileSpreadsheet,
      color: "bg-green-100 text-green-700"
    },
    { 
      id: "api", 
      nombre: "API de ERP", 
      descripcion: "Conexión directa con Softland, Tango, Bejerman, SAP u otro ERP",
      icon: Database,
      color: "bg-blue-100 text-blue-700"
    },
    { 
      id: "txt", 
      nombre: "Archivo de Texto", 
      descripcion: "Archivos .txt o .csv con separadores",
      icon: FileText,
      color: "bg-orange-100 text-orange-700"
    }
  ];

  const tiposImportacion = [
    {
      id: "contable",
      nombre: "Saldos Contables",
      descripcion: "Plan de cuentas y saldos iniciales de cada cuenta",
      icon: Building2,
      campos: ["Código cuenta", "Descripción", "Tipo", "Nivel", "Saldo Debe", "Saldo Haber"]
    },
    {
      id: "terceros",
      nombre: "Clientes y Proveedores",
      descripcion: "Maestro de terceros con saldos de cuenta corriente",
      icon: Users,
      campos: ["Código", "Razón Social", "CUIT", "Condición IVA", "Email", "Teléfono", "Saldo"]
    },
    {
      id: "stock",
      nombre: "Stock de Artículos",
      descripcion: "Inventario inicial con cantidades y costos",
      icon: Package,
      campos: ["Código artículo", "Descripción", "Categoría", "Unidad", "Cantidad", "Costo unitario", "Depósito"]
    }
  ];

  const estructurasEjemplo = {
    contable: [
      { codigo: "1", descripcion: "ACTIVO", tipo: "A", nivel: 1, debe: "", haber: "" },
      { codigo: "1.1", descripcion: "ACTIVO CORRIENTE", tipo: "A", nivel: 2, debe: "", haber: "" },
      { codigo: "1.1.01", descripcion: "Caja y Bancos", tipo: "A", nivel: 3, debe: "", haber: "" },
      { codigo: "1.1.01.001", descripcion: "Caja en Pesos", tipo: "A", nivel: 4, debe: "150000.00", haber: "" },
      { codigo: "1.1.01.002", descripcion: "Banco Nación Cta Cte", tipo: "A", nivel: 4, debe: "2500000.00", haber: "" },
      { codigo: "2", descripcion: "PASIVO", tipo: "P", nivel: 1, debe: "", haber: "" },
      { codigo: "2.1.01.001", descripcion: "Proveedores Varios", tipo: "P", nivel: 4, debe: "", haber: "850000.00" },
    ],
    terceros: [
      { codigo: "P001", razon_social: "DISTRIBUIDORA MEDICA SRL", cuit: "30-71234567-8", condicion: "RI", email: "admin@distmed.com.ar", telefono: "011-4555-1234", saldo: "-250000.00" },
      { codigo: "P002", razon_social: "LABORATORIOS OPTICOS SA", cuit: "30-65432198-5", condicion: "RI", email: "ventas@labopt.com.ar", telefono: "011-4666-5678", saldo: "-180000.00" },
      { codigo: "C001", razon_social: "CLINICA OFTALMOLOGICA NORTE", cuit: "30-77889944-2", condicion: "RI", email: "admin@clinorte.com.ar", telefono: "011-4777-9999", saldo: "95000.00" },
    ],
    stock: [
      { codigo: "ART001", descripcion: "Lente Intraocular Monofocal", categoria: "Insumos Quirúrgicos", unidad: "UN", cantidad: "50", costo: "15000.00", deposito: "Central" },
      { codigo: "ART002", descripcion: "Solución Viscoelástica", categoria: "Insumos Quirúrgicos", unidad: "UN", cantidad: "100", costo: "8500.00", deposito: "Central" },
      { codigo: "ART003", descripcion: "Libro Oftalmología Clínica", categoria: "Publicaciones", unidad: "UN", cantidad: "200", costo: "12000.00", deposito: "Publicaciones" },
    ]
  };

  const handleSimularImportacion = () => {
    setProcesando(true);
    setProgreso(0);
    
    const interval = setInterval(() => {
      setProgreso(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setProcesando(false);
          toast.success("Importación simulada completada", {
            description: "Esta es una demostración. La importación real se configurará según sus necesidades."
          });
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  const resetear = () => {
    setPaso(1);
    setOrigenDatos(null);
    setTipoImportacion(null);
    setProgreso(0);
  };

  const getStepClass = (step) => {
    return paso >= step ? 'bg-[#2853B9] text-white' : 'bg-gray-200 text-gray-500';
  };

  const getLineClass = (step) => {
    return paso > step ? 'bg-[#2853B9]' : 'bg-gray-200';
  };

  const getOrigenClass = (origenId) => {
    return origenDatos === origenId ? 'ring-2 ring-[#2853B9] bg-blue-50' : '';
  };

  const getTipoClass = (tipoId) => {
    return tipoImportacion === tipoId ? 'ring-2 ring-[#2853B9] bg-blue-50' : '';
  };

  const getSaldoClass = (saldo) => {
    return parseFloat(saldo) < 0 ? 'text-red-600' : 'text-green-600';
  };

  return (
    <div className="space-y-6" data-testid="importacion-saldos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Importación de Saldos Iniciales</h1>
          <p className="text-gray-600">Migre datos desde su sistema anterior</p>
        </div>
        {paso > 1 && (
          <Button variant="outline" onClick={resetear}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Reiniciar
          </Button>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getStepClass(step)}`}>
              {paso > step ? <CheckCircle2 className="w-5 h-5" /> : step}
            </div>
            {step < 3 && (
              <div className={`w-16 h-1 ${getLineClass(step)}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-8 text-sm text-gray-600">
        <span className={paso >= 1 ? 'text-[#2853B9] font-medium' : ''}>Origen</span>
        <span className={paso >= 2 ? 'text-[#2853B9] font-medium' : ''}>Tipo de Datos</span>
        <span className={paso >= 3 ? 'text-[#2853B9] font-medium' : ''}>Importar</span>
      </div>

      <AnimatePresence mode="wait">
        {/* PASO 1: Seleccionar origen */}
        {paso === 1 && (
          <motion.div
            key="paso1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="cao-card">
              <CardHeader>
                <CardTitle>¿De dónde provienen los datos?</CardTitle>
                <CardDescription>
                  Seleccione el formato de origen de los datos a importar
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {origenes.map((origen) => (
                    <Card 
                      key={origen.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${getOrigenClass(origen.id)}`}
                      onClick={() => setOrigenDatos(origen.id)}
                      data-testid={`origen-${origen.id}`}
                    >
                      <CardContent className="p-6 text-center">
                        <div className={`w-16 h-16 rounded-full ${origen.color} mx-auto mb-4 flex items-center justify-center`}>
                          <origen.icon className="w-8 h-8" />
                        </div>
                        <h3 className="font-semibold mb-2">{origen.nombre}</h3>
                        <p className="text-sm text-gray-600">{origen.descripcion}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {origenDatos === "api" && (
                  <Alert className="mt-4">
                    <Info className="w-4 h-4" />
                    <AlertTitle>Integración con ERP</AlertTitle>
                    <AlertDescription>
                      La conexión directa con su ERP requiere configuración específica. 
                      Nuestro equipo técnico lo asistirá en el proceso de integración.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-end mt-6">
                  <Button 
                    onClick={() => setPaso(2)} 
                    disabled={!origenDatos}
                    className="bg-[#2853B9] hover:bg-[#1E3876]"
                  >
                    Continuar
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* PASO 2: Tipo de importación */}
        {paso === 2 && (
          <motion.div
            key="paso2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="cao-card">
              <CardHeader>
                <CardTitle>¿Qué datos desea importar?</CardTitle>
                <CardDescription>
                  Seleccione el tipo de información a migrar
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {tiposImportacion.map((tipo) => (
                    <Card 
                      key={tipo.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${getTipoClass(tipo.id)}`}
                      onClick={() => setTipoImportacion(tipo.id)}
                      data-testid={`tipo-${tipo.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-lg bg-[#2853B9]/10 flex items-center justify-center">
                            <tipo.icon className="w-6 h-6 text-[#2853B9]" />
                          </div>
                          <h3 className="font-semibold">{tipo.nombre}</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{tipo.descripcion}</p>
                        <div className="flex flex-wrap gap-1">
                          {tipo.campos.slice(0, 4).map((campo, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{campo}</Badge>
                          ))}
                          {tipo.campos.length > 4 && (
                            <Badge variant="outline" className="text-xs">+{tipo.campos.length - 4}</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setPaso(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Anterior
                  </Button>
                  <Button 
                    onClick={() => setPaso(3)} 
                    disabled={!tipoImportacion}
                    className="bg-[#2853B9] hover:bg-[#1E3876]"
                  >
                    Continuar
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* PASO 3: Importar */}
        {paso === 3 && (
          <motion.div
            key="paso3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Resumen de selección */}
            <Card className="cao-card">
              <CardHeader>
                <CardTitle>Resumen de Importación</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <Badge className="bg-[#2853B9]">
                    {origenes.find(o => o.id === origenDatos)?.nombre}
                  </Badge>
                  <Badge variant="outline">
                    {tiposImportacion.find(t => t.id === tipoImportacion)?.nombre}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Estructura esperada */}
            <Card className="cao-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Estructura del Archivo Esperada
                </CardTitle>
                <CardDescription>
                  Su archivo debe contener las siguientes columnas (formato típico de ERPs argentinos)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {tipoImportacion === "contable" && (
                          <>
                            <TableHead>Código</TableHead>
                            <TableHead>Descripción</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Nivel</TableHead>
                            <TableHead className="text-right">Saldo Debe</TableHead>
                            <TableHead className="text-right">Saldo Haber</TableHead>
                          </>
                        )}
                        {tipoImportacion === "terceros" && (
                          <>
                            <TableHead>Código</TableHead>
                            <TableHead>Razón Social</TableHead>
                            <TableHead>CUIT</TableHead>
                            <TableHead>Cond. IVA</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Teléfono</TableHead>
                            <TableHead className="text-right">Saldo</TableHead>
                          </>
                        )}
                        {tipoImportacion === "stock" && (
                          <>
                            <TableHead>Código</TableHead>
                            <TableHead>Descripción</TableHead>
                            <TableHead>Categoría</TableHead>
                            <TableHead>Unidad</TableHead>
                            <TableHead className="text-right">Cantidad</TableHead>
                            <TableHead className="text-right">Costo Unit.</TableHead>
                            <TableHead>Depósito</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tipoImportacion === "contable" && estructurasEjemplo.contable.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-sm">{row.codigo}</TableCell>
                          <TableCell>{row.descripcion}</TableCell>
                          <TableCell>{row.tipo}</TableCell>
                          <TableCell>{row.nivel}</TableCell>
                          <TableCell className="text-right font-mono">{row.debe}</TableCell>
                          <TableCell className="text-right font-mono">{row.haber}</TableCell>
                        </TableRow>
                      ))}
                      {tipoImportacion === "terceros" && estructurasEjemplo.terceros.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-sm">{row.codigo}</TableCell>
                          <TableCell>{row.razon_social}</TableCell>
                          <TableCell className="font-mono text-sm">{row.cuit}</TableCell>
                          <TableCell>{row.condicion}</TableCell>
                          <TableCell className="text-sm">{row.email}</TableCell>
                          <TableCell>{row.telefono}</TableCell>
                          <TableCell className={`text-right font-mono ${getSaldoClass(row.saldo)}`}>
                            {row.saldo}
                          </TableCell>
                        </TableRow>
                      ))}
                      {tipoImportacion === "stock" && estructurasEjemplo.stock.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-sm">{row.codigo}</TableCell>
                          <TableCell>{row.descripcion}</TableCell>
                          <TableCell>{row.categoria}</TableCell>
                          <TableCell>{row.unidad}</TableCell>
                          <TableCell className="text-right font-mono">{row.cantidad}</TableCell>
                          <TableCell className="text-right font-mono">{row.costo}</TableCell>
                          <TableCell>{row.deposito}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Descargar Plantilla Excel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Zona de carga */}
            <Card className="cao-card">
              <CardHeader>
                <CardTitle>Cargar Archivo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#2853B9] transition-colors">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-2">
                    Arrastre su archivo aquí o haga clic para seleccionar
                  </p>
                  <p className="text-sm text-gray-500">
                    Formatos aceptados: .xlsx, .xls, .csv
                  </p>
                  <input type="file" className="hidden" accept=".xlsx,.xls,.csv" />
                  <Button className="mt-4 bg-[#2853B9] hover:bg-[#1E3876]">
                    Seleccionar Archivo
                  </Button>
                </div>

                {procesando && (
                  <div className="mt-6 space-y-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Procesando importación...</span>
                    </div>
                    <Progress value={progreso} className="h-2" />
                    <p className="text-sm text-gray-500">{progreso}% completado</p>
                  </div>
                )}

                <Alert className="mt-6">
                  <AlertCircle className="w-4 h-4" />
                  <AlertTitle>Módulo en Desarrollo</AlertTitle>
                  <AlertDescription>
                    Este módulo será personalizado según las necesidades específicas de su organización. 
                    Nuestro equipo técnico configurará la importación para adaptarse al formato de datos 
                    de su sistema de origen (Softland, Tango, Bejerman, u otro).
                  </AlertDescription>
                </Alert>

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setPaso(2)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Anterior
                  </Button>
                  <Button 
                    onClick={handleSimularImportacion}
                    disabled={procesando}
                    className="bg-[#2853B9] hover:bg-[#1E3876]"
                  >
                    {procesando ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Simular Importación
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImportacionSaldos;
